import { useJsApiLoader } from '@react-google-maps/api';
import { brand } from '../../../theme/tokens';

/** Browser key for the Maps JavaScript API. Restrict it to the admin's domains in Google Cloud. */
export const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyCpAB1kZf0hvttCDcVXHKT1mrPDkVOAeOA';

/** Default center when there is nothing to fit (Caracas). */
export const DEFAULT_MAP_CENTER = { lat: 10.5, lng: -66.9 };

/** One loader for every map in the admin (avoids reloading the script per page). */
export function useShelfMapsLoader() {
    return useJsApiLoader({ id: 'shelf-google-maps', googleMapsApiKey: GOOGLE_MAPS_API_KEY });
}

/** Light, quiet base map in the Shelf palette: land, roads and water only. */
export const MAP_STYLES = [
    { elementType: 'geometry', stylers: [{ color: '#f3f5f8' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#5b6676' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f3f5f8' }] },
    { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#d5dbe3' }] },
    { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#344054' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ visibility: 'on' }, { color: '#e4efe7' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#7a8596' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#e8edf3' }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#d7dee8' }] },
    { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9aa4b2' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#cfe0ef' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#7d93a8' }] }
];

/** Base options shared by every map. */
export const BASE_MAP_OPTIONS = {
    styles: MAP_STYLES,
    disableDefaultUI: true,
    zoomControl: true,
    fullscreenControl: true,
    clickableIcons: false,
    gestureHandling: 'cooperative',
    backgroundColor: '#f3f5f8'
};

/** Pin colors by meaning. */
export const PIN_TONES = Object.freeze({
    neutral: brand[600],
    success: '#16a34a',
    danger: '#dc2626'
});

const svgUrl = (svg) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

/**
 * Teardrop pin as a Google Maps icon: tone fill, white ring and center dot, soft shadow.
 * @param {string} color - fill color
 * @param {boolean} [selected]
 */
export function pinIcon(color, selected = false) {
    const w = selected ? 40 : 30;
    const h = Math.round(w * 1.3);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 30 39">
<defs><filter id="s" x="-30%" y="-20%" width="160%" height="150%"><feDropShadow dx="0" dy="1.5" stdDeviation="1.4" flood-color="#0b1726" flood-opacity="0.35"/></filter></defs>
<path filter="url(#s)" d="M15 2C8.4 2 3 7.2 3 13.7c0 8.6 10.3 21 11.1 22a1.2 1.2 0 0 0 1.8 0C16.7 34.7 27 22.3 27 13.7 27 7.2 21.6 2 15 2z" fill="${color}" stroke="#fff" stroke-width="2"/>
<circle cx="15" cy="13.6" r="${selected ? 4.6 : 4}" fill="#fff"/></svg>`;
    const size = window.google?.maps ? new window.google.maps.Size(w, h) : undefined;
    const anchor = window.google?.maps ? new window.google.maps.Point(w / 2, h - 1) : undefined;
    return { url: svgUrl(svg), scaledSize: size, anchor };
}

/** Cluster bubbles (navy, white ring) for MarkerClusterer, small → large. */
export const CLUSTER_STYLES = [36, 44, 54].map((size) => ({
    url: svgUrl(
        `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 1}" fill="${brand[600]}" fill-opacity="0.18"/>
<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 6}" fill="${brand[600]}" stroke="#fff" stroke-width="2"/></svg>`
    ),
    height: size,
    width: size,
    textColor: '#ffffff',
    textSize: size > 40 ? 13 : 12,
    fontWeight: '600',
    fontFamily: 'Inter, system-ui, sans-serif'
}));

/**
 * Fit the map to `points` ({lat, lng}); a single point gets a street-level zoom.
 * @param {google.maps.Map|null} map
 * @param {{lat:number,lng:number}[]} points
 */
export function fitMapToPoints(map, points) {
    if (!map || !window.google?.maps || !points?.length) return;
    if (points.length === 1) {
        map.setCenter(points[0]);
        map.setZoom(15);
        return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 56);
}
